import React, { useEffect, useRef, useState, useCallback } from 'react';

function parseLyrics(text) {
  const items = [];
  (text || '').split('\n').forEach((ln) => {
    const s = ln.trim();
    if (!s) return;
    if (/^\[\[.*\]\]$/.test(s)) return;
    const m = s.match(/^\[(\d+(?:\.\d+)?)?\s*:[^\]]*\]\s*(.*)$/);
    if (m) {
      const start = (m[1] != null && m[1] !== '') ? parseFloat(m[1]) : null;
      const txt = (m[2] || '').trim();
      if (txt) items.push({ start, text: txt });
    } else {
      items.push({ start: null, text: s });
    }
  });
  const n = items.length;
  let i = 0;
  while (i < n) {
    if (items[i].start == null) { i += 1; continue; }
    let j = i + 1;
    while (j < n && items[j].start == null) j += 1;
    const startT = items[i].start;
    const span = j - i;
    const endT = j < n ? items[j].start : startT + span * 3.5;
    for (let k = 0; k < span; k += 1) {
      items[i + k].start = startT + ((endT - startT) * k) / span;
    }
    i = j;
  }
  for (let k = 0; k < n; k += 1) { if (items[k].start == null) items[k].start = 0; }
  return items;
}

export default function RadioSection({ t, onClose, open, onCreateFromOrder, onRecognize, onOpenWord }) {
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptWord, setPromptWord] = useState('');
  const [promptBusy, setPromptBusy] = useState(false);
  const [promptMsg, setPromptMsg] = useState('');
  const [promptDone, setPromptDone] = useState(false);
  const [createdSong, setCreatedSong] = useState(null);
  const [promptVariants, setPromptVariants] = useState(null);
  const [promptError, setPromptError] = useState(false);
  const [lastAttempt, setLastAttempt] = useState(null);
  const [songs, setSongs] = useState(null);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [started, setStarted] = useState(false);
  const [curLine, setCurLine] = useState(-1);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  const audioRef = useRef(null);
  const boxRef = useRef(null);
  const recentRef = useRef([]);
  const timerRef = useRef(null);
  const stoppedRef = useRef(false);

  const COOLDOWN = 10;
  const GAP = 8;

  const lines = parseLyrics(current ? current.lyrics : '');

  useEffect(() => {
    let cancelled = false;
    fetch('/api/ai/radio', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        if (d && Array.isArray(d.songs)) setSongs(d.songs);
        else setSongs([]);
      })
      .catch(() => { if (!cancelled) setSongs([]); });
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      const a = audioRef.current;
      if (a) { try { a.pause(); } catch (e) {} }
    };
  }, []);

  const pickNext = useCallback(() => {
    const list = songs || [];
    if (!list.length) return null;
    const cd = Math.min(COOLDOWN, list.length - 1);
    const recent = recentRef.current.slice(-cd);
    let pool = list.filter((s) => !recent.includes(s.url));
    if (!pool.length) pool = list;
    const chosen = pool[Math.floor(Math.random() * pool.length)];
    recentRef.current.push(chosen.url);
    if (recentRef.current.length > 60) recentRef.current = recentRef.current.slice(-60);
    return chosen;
  }, [songs]);

  const playSong = useCallback((song) => {
    if (!song) return;
    stoppedRef.current = false;
    setCountdown(0);
    setCurLine(-1);
    setCur(0);
    setDur(0);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setCurrent(song);
    const a = audioRef.current;
    if (!a) return;
    a.src = song.url;
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, []);

  const scheduleNext = useCallback(() => {
    if (stoppedRef.current) return;
    setPlaying(false);
    let left = GAP;
    setCountdown(left);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      left -= 1;
      if (left <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setCountdown(0);
        if (!stoppedRef.current) playSong(pickNext());
      } else {
        setCountdown(left);
      }
    }, 1000);
  }, [pickNext, playSong]);

  const start = () => { setStarted(true); playSong(pickNext()); };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      stoppedRef.current = true;
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setCountdown(0);
      try { a.pause(); } catch (e) {}
      setPlaying(false);
      return;
    }
    if (countdown > 0) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setCountdown(0);
      playSong(pickNext());
      return;
    }
    if (!started || !current) { start(); return; }
    stoppedRef.current = false;
    a.play().then(() => setPlaying(true)).catch(() => {});
  };

  const onTime = () => {
    const a = audioRef.current;
    if (!a) return;
    setCur(a.currentTime || 0);
    if (!lines.length) return;
    const tt = a.currentTime;
    const lastStart = lines[lines.length - 1].start || 0;
    let i = -1;
    if (lastStart <= 0.01 && a.duration && isFinite(a.duration)) {
      i = Math.min(lines.length - 1, Math.floor((tt / a.duration) * lines.length));
    } else {
      for (let k = 0; k < lines.length; k += 1) { if (lines[k].start != null && lines[k].start <= tt + 0.15) i = k; }
    }
    if (i !== curLine) setCurLine(i);
  };

  const onLoaded = () => { const a = audioRef.current; if (a && isFinite(a.duration)) setDur(a.duration); };
  const onSeek = (e) => { const a = audioRef.current; const v = Number(e.target.value); if (a) a.currentTime = v; setCur(v); };

  useEffect(() => {
    const box = boxRef.current;
    if (!box || curLine < 0) return;
    const el = box.children[curLine];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [curLine]);

  const skipToStart = () => {
    const a = audioRef.current;
    if (!a) return;
    stoppedRef.current = false;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setCountdown(0);
    a.currentTime = 0;
    a.play().then(() => setPlaying(true)).catch(() => {});
  };

  const skipNext = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setCountdown(0);
    stoppedRef.current = false;
    playSong(pickNext());
  };

  const download = async () => {
    if (!current) return;
    try {
      const r = await fetch(current.url);
      const b = await r.blob();
      const u = URL.createObjectURL(b);
      const el = document.createElement('a');
      el.href = u;
      el.download = (current.word || 'song') + '.mp3';
      el.click();
      URL.revokeObjectURL(u);
    } catch (e) {
      window.open(current.url, '_blank');
    }
  };

  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

  const VOICE = { male: 'мужской голос', female: 'женский голос', duet: 'дуэт' };
  const STYLE = { pop: 'поп', rock: 'рок', reggae: 'регги', lullaby: 'колыбельная', acoustic: 'акустика', rap: 'рэп', folk: 'фолк', hit: 'хит', depeche: 'Depeche Mode', nordic: 'скандинавские мотивы' };
  const MOOD = { happy: 'весёлое', energetic: 'энергичное', calm: 'спокойное', dreamy: 'мечтательное', sad: 'грустное', romantic: 'романтичное', epic: 'эпичное' };
  const TEMPO = { slow: 'медленный темп', medium: 'средний темп', fast: 'быстрый темп' };
  const MANNER = { sung: 'с пением', recite: 'речитатив' };
  const orderText = (f) => {
    if (!f) return '';
    const parts = [STYLE[f.style] || f.style, VOICE[f.voice] || f.voice, MOOD[f.mood] || f.mood, TEMPO[f.tempo] || f.tempo, MANNER[f.manner] || f.manner];
    return parts.filter(Boolean).join(' · ');
  };
  const createForVariant = async (cardWord, pos) => {
    setPromptVariants(null);
    setPromptError(false);
    setLastAttempt({ cardWord, pos });
    setPromptBusy(true);
    try {
      const result = await onCreateFromOrder(cardWord, pos, current, (m) => setPromptMsg(m));
      recentRef.current = [];
      let fresh = [];
      try {
        const r = await fetch('/api/ai/radio', { cache: 'no-store' });
        const d = await r.json();
        if (d && Array.isArray(d.songs)) { fresh = d.songs; setSongs(fresh); }
      } catch (e) {}
      let made = null;
      if (result && result.url) {
        const h = (result.url.split('/').pop() || '').split('?')[0];
        made = fresh.find((s) => s.url && (s.url.split('/').pop() || '').split('?')[0] === h) || null;
      }
      if (!made && result) {
        const rw = String(result.word || cardWord).toLowerCase();
        made = fresh.find((s) => String(s.word || '').toLowerCase() === rw) || null;
      }
      setCreatedSong(made);
      setPromptDone(true);
      setPromptMsg('Готово! Песня создана.');
      setPromptBusy(false);
    } catch (e) {
      setPromptMsg('Не удалось создать песню — возможно, прервалась связь. Нажмите «Повторить».');
      setPromptError(true);
      setPromptBusy(false);
    }
  };

  const doCreate = async () => {
    const w = (promptWord || '').trim();
    if (!w || !current || !onCreateFromOrder) return;
    setPromptBusy(true);
    setPromptError(false);
    setPromptMsg('Распознаём слово…');
    try {
      let variants = [{ word: w, pos: 'noun' }];
      if (onRecognize) {
        const rec = await onRecognize(w);
        if (rec && Array.isArray(rec.variants) && rec.variants.length) variants = rec.variants;
      }
      if (variants.length > 1) { setPromptVariants(variants); setPromptMsg(''); setPromptBusy(false); return; }
      await createForVariant(variants[0].word || w, variants[0].pos || 'noun');
    } catch (e) {
      setPromptMsg('Не удалось: ' + ((e && e.message) || 'ошибка'));
      setPromptBusy(false);
    }
  };
  const fmt = (s) => { s = Math.max(0, Math.floor(s || 0)); return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'); };
  const pct = dur ? (cur / dur) * 100 : 0;

  return (
    <section className={"radio-view" + (open ? "" : " radio-hidden")}>
      <div className="radio-head">
        <h2 className="radio-title">{t('radio')}</h2>
        <button type="button" className="radio-close" aria-label="Закрыть" onClick={onClose}>×</button>
      </div>

      {songs === null && <p className="radio-info">{t('radioLoading')}</p>}
      {songs !== null && songs.length === 0 && <p className="radio-info">{t('radioEmpty')}</p>}

      {songs !== null && songs.length > 0 && (
        <div className="radio-player">
          <div className="radio-word">{current ? (onOpenWord ? <button type="button" className="radio-word-link" onClick={() => onOpenWord(current)} title="Открыть страницу слова">{cap(current.disp || current.word)}</button> : cap(current.disp || current.word)) : '—'}</div>
          {current && current.tr && current.tr.length > 0 && (
            <div className="radio-tr">{current.tr.join(', ')}</div>
          )}
          {countdown > 0 && (
            <div className="radio-countdown">{t('radioNext')} {countdown}</div>
          )}

          <audio
            ref={audioRef}
            className="radio-audio-native"
            preload="none"
            onEnded={scheduleNext}
            onTimeUpdate={onTime}
            onLoadedMetadata={onLoaded}
            onPlay={() => setPlaying(true)}
          />

          <div className="radio-seek-row">
            <span className="radio-time">{fmt(cur)}</span>
            <input
              type="range"
              className="song-ctl-seek"
              min="0"
              max={dur || 0}
              step="0.1"
              value={cur}
              onChange={onSeek}
              aria-label="Перемотка"
              style={{ background: 'linear-gradient(to right, #8b5cf6 ' + pct + '%, rgba(139,92,246,.22) ' + pct + '%)' }}
            />
            <span className="radio-time">{fmt(dur)}</span>
          </div>

          <div className="radio-transport">
            <button type="button" className="radio-ctl" onClick={skipToStart} disabled={!current} aria-label="В начало">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><polygon points="19 20 9 12 19 4 19 20"/><rect x="4" y="4" width="2.4" height="16" rx="1"/></svg>
            </button>
            <button type="button" className="radio-ctl radio-ctl-main" onClick={togglePlay} aria-label={playing ? 'Пауза' : 'Играть'}>
              {playing
                ? <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4.5" height="16" rx="1.2"/><rect x="13.5" y="4" width="4.5" height="16" rx="1.2"/></svg>
                : <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor"><polygon points="7 4 20 12 7 20 7 4"/></svg>}
            </button>
            <button type="button" className="radio-ctl" onClick={skipNext} disabled={!songs || !songs.length} aria-label="Следующая">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 4 15 12 5 20 5 4"/><rect x="17.6" y="4" width="2.4" height="16" rx="1"/></svg>
            </button>
          </div>
          {current && (
            <div className="radio-action-row">
              {onCreateFromOrder && (
                <button type="button" className="radio-btn radio-btn-prompt" onClick={() => { setPromptWord(''); setPromptMsg(''); setPromptBusy(false); setPromptDone(false); setCreatedSong(null); setPromptVariants(null); setPromptError(false); setLastAttempt(null); setPromptOpen(true); }}>Промт</button>
              )}
              <button type="button" className="radio-save" onClick={download} aria-label="Скачать песню" title="Скачать песню">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              </button>
            </div>
          )}
          {lines.length > 0 && (
            <div className="song-lyrics radio-lyrics" ref={boxRef}>
              {lines.map((l, i) => (
                <div key={i} className={'song-line' + (i === curLine ? ' song-line-active' : '')}>{l.text}</div>
              ))}
            </div>
          )}
          {current && current.cust && current.cust.nick && (
            <div className="radio-customer">
              <span className="radio-customer-label">Заказчик:</span>
              {current.cust.avatar ? (
                <img className="radio-customer-ava" src={current.cust.avatar} alt={current.cust.nick} />
              ) : (
                <span className="radio-customer-ava radio-customer-initial">{current.cust.nick.charAt(0).toUpperCase()}</span>
              )}
              <span className="radio-customer-nick">{current.cust.nick}</span>
            </div>
          )}
        </div>
      )}
      {promptOpen && (
        <div className="rp-overlay" onClick={() => { if (!promptBusy) setPromptOpen(false); }}>
          <div className="rp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rp-head">
              <h3 className="rp-title">Своя песня в этом стиле</h3>
              <button type="button" className="rp-close" aria-label="Закрыть" onClick={() => { if (!promptBusy) setPromptOpen(false); }}>×</button>
            </div>
            <p className="rp-order">Заказ: {orderText(current && current.filters)}</p>
            <p className="rp-hint">Введите слово — Evik создаст для него песню в таком же стиле. Если слова ещё нет в базе, он сначала создаст карточку слова.</p>
            <input className="rp-input" type="text" value={promptWord} placeholder="норвежское слово…" disabled={promptBusy} onChange={(e) => setPromptWord(e.target.value)} />
            {promptMsg && <p className="rp-status">{promptMsg}</p>}
            {promptVariants ? (
              <div className="rp-variants">
                <p className="rp-vhint">У слова несколько значений — выберите нужное:</p>
                {promptVariants.map((v, i) => (
                  <button key={i} type="button" className="rp-variant" disabled={promptBusy} onClick={() => createForVariant((v.word || promptWord).toLowerCase(), v.pos || 'noun')}>
                    <strong>{v.word}</strong> · {v.pos_ru || v.pos}{v.short_meaning ? ' — ' + v.short_meaning : ''}
                  </button>
                ))}
              </div>
            ) : promptError ? (
              <button type="button" className="rp-create" disabled={promptBusy} onClick={() => { if (lastAttempt) createForVariant(lastAttempt.cardWord, lastAttempt.pos); }}>{promptBusy ? 'Создаём…' : 'Повторить'}</button>
            ) : promptDone ? (
              <button type="button" className="rp-create" onClick={() => { setPromptOpen(false); if (createdSong) { setStarted(true); playSong(createdSong); } }}>Слушать песню</button>
            ) : (
              <button type="button" className="rp-create" onClick={doCreate} disabled={promptBusy || !promptWord.trim()}>
                {promptBusy ? 'Создаём…' : 'Создать песню'}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
